/**
 * @fileoverview Hook for managing user personas
 * 
 * Provides access to user persona data for hyper-personalized AI experiences.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface PersonaIdentity {
  display_name: string | null;
  gender: string | null;
  clothing_size: string | null;
  country: string | null;
  currency: string | null;
}

export interface PersonaBehavior {
  saves_count: number;
  tryons_count: number;
  polls_created: number;
  chat_sessions: number;
  avg_price_point: number | null;
  price_range: { min: number | null; max: number | null };
  preferred_stores: string[];
  feature_affinity: string[];
  avg_dwell_seconds: number | null;
  high_interest_items: string[];
  share_count: number;
  share_targets: string[];
}

export interface PersonaPreferences {
  favorite_categories: string[];
  favorite_brands: string[];
  style_keywords: string[];
  recent_interests: Array<{ topic: string; date: string }>;
}

export interface PersonaSignals {
  last_10_searches: string[];
  last_5_saves: Array<{ name: string; category: string | null; price: number | null }>;
  poll_voting_patterns: Record<string, number>;
  regret_signals: {
    count: number;
    regret_rate: number;
    common_regret_categories: string[];
  };
  voting_style: {
    total_votes: number;
    preference_map: Record<string, number>;
  };
}

export interface PersonaData {
  version: number;
  compiled_at: string;
  identity: PersonaIdentity;
  style_dna: Record<string, any>;
  behavior: PersonaBehavior;
  preferences: PersonaPreferences;
  signals: PersonaSignals;
  ai_notes: string;
}

export interface UserPersona {
  id: string;
  user_id: string;
  persona_data: PersonaData;
  last_compiled_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

export function useUserPersona(userId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const targetUserId = userId || user?.id;

  const { data: persona, isLoading, error } = useQuery({
    queryKey: ['user-persona', targetUserId],
    queryFn: async () => {
      if (!targetUserId) return null;

      const { data, error } = await supabase
        .from('user_personas')
        .select('*')
        .eq('user_id', targetUserId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      
      return {
        ...data,
        persona_data: data.persona_data as unknown as PersonaData,
      } as UserPersona;
    },
    enabled: !!targetUserId,
  });

  const compilePersona = useMutation({
    mutationFn: async (targetId?: string) => {
      const id = targetId || targetUserId;
      if (!id) throw new Error('No user ID provided');

      const { data, error } = await supabase.functions.invoke('compile-user-persona', {
        body: { user_id: id }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-persona', targetUserId] });
      // Updated silently
    },
    onError: () => {
      toast.error("Couldn't update your style profile. Try again?");
    },
  });

  const updateAiNotes = useMutation({
    mutationFn: async ({ userId: id, notes }: { userId: string; notes: string }) => {
      // Get current persona data
      const { data: current, error: fetchError } = await supabase
        .from('user_personas')
        .select('persona_data')
        .eq('user_id', id)
        .single();

      if (fetchError) throw fetchError;

      // Update AI notes in persona data
      const currentData = current?.persona_data as unknown as PersonaData;
      const updatedPersonaData = {
        ...currentData,
        ai_notes: notes,
      };

      const { error } = await (supabase
        .from('user_personas')
        .update({ persona_data: updatedPersonaData } as any)
        .eq('user_id', id));

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-persona'] });
      // Saved silently
    },
    onError: () => {
      toast.error("Couldn't save notes. Try again?");
    },
  });

  return {
    persona,
    personaData: persona?.persona_data as PersonaData | undefined,
    isLoading,
    error,
    compilePersona,
    updateAiNotes,
  };
}

// Admin hook for listing all personas
export function useAllUserPersonas() {
  return useQuery({
    queryKey: ['all-user-personas'],
    queryFn: async () => {
      // Fetch personas
      const { data: personas, error } = await supabase
        .from('user_personas')
        .select('*')
        .order('last_compiled_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      
      // Fetch profiles separately for the user_ids
      const userIds = (personas || []).map(p => p.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', userIds);
      
      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));
      
      return (personas || []).map(p => ({
        ...p,
        persona_data: p.persona_data as unknown as PersonaData,
        profiles: profileMap.get(p.user_id) || null,
      })) as (UserPersona & { profiles: { display_name: string | null; avatar_url: string | null } | null })[];
    },
  });
}
